import React, { useMemo } from 'react';
import { Anchor, Box, Hint, NotVisibleICount, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { Option } from '@/common/types';
import { IEmployerCalendarEventDtoCategoryEnum, IInvoiceStatusType } from '@/dtos';
import theme from '@/styles/theme';

import { EmployerCalendarPayload } from '../EmployerCalendar.types';

interface Props {
  isPast?: boolean;

  event: EmployerCalendarPayload;
  isDateMode?: boolean;
  isDateFromNow?: boolean;
  isViewPermissions?: boolean;
}

const MAX_DISPLAYED_DAYS = 5;

const EmployerCalendarEventsItem: React.FC<Props> = ({
  isPast,
  event,
  isDateMode,
  isDateFromNow,
  isViewPermissions,
}) => {
  const currentColor = useMemo(() => {
    if (event.category === IEmployerCalendarEventDtoCategoryEnum.FUNDING) {
      return 'fund2';
    }
    if (event.category === IEmployerCalendarEventDtoCategoryEnum.PLAN_DATES) {
      return 'fund3';
    }
    return '';
  }, [event.category]);

  const isFailedMode = useMemo(() => {
    if (
      event.invoiceStatus === IInvoiceStatusType.PAYMENT_FAILED ||
      event.invoiceStatus === IInvoiceStatusType.PAYMENT_REJECTED
    ) {
      return true;
    }
    return false;
  }, [event.invoiceStatus]);
  const planName = useMemo(() => {
    if (event.plans?.length && event.plans.length > 1) {
      const options: Option[] = event.plans.map((plan, index) => ({
        key: index,
        value: `${plan.name || ''}`,
        textStyle: {
          color: isPast ? 'textDisabled' : _.get(theme.colors.cardTypes, plan?.type || 'HSA'),
        },
      }));
      return (
        <NotVisibleICount
          key={event.id}
          options={options}
          textStyle={{
            color: 'textSecondary',
            style: {
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            },
          }}
        />
      );
    }
    return event.plans?.[0]?.name || '';
  }, [event.id, event.plans, isPast]);

  const dateTime = useMemo(() => {
    const date = event.eventDate;

    if (date) {
      if (isPast) {
        return (
          <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
            {date.format(DEFAULT_DATE_FORMAT)}
          </Text>
        );
      }
      if (date.isSame(dayjs(), 'day') && !isDateMode) {
        return (
          <Hint
            hintWidth="140px"
            hintElement={
              <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
                Today
              </Text>
            }
          >
            <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
              {date.format(DEFAULT_DATE_FORMAT)}
            </Text>
          </Hint>
        );
      }
      if (isDateFromNow) {
        const diffDays = date.diff(dayjs(), 'day');
        if (diffDays > MAX_DISPLAYED_DAYS) {
          return (
            <Hint
              hintWidth="140px"
              hintElement={
                <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
                  {date.fromNow()}
                </Text>
              }
            >
              <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
                {date.format(DEFAULT_DATE_FORMAT)}
              </Text>
            </Hint>
          );
        }
        return (
          <Hint
            hintWidth="140px"
            hintElement={
              <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
                {diffDays > 1 ? `in ${diffDays} days` : 'in a day'}
              </Text>
            }
          >
            <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
              {date.format(DEFAULT_DATE_FORMAT)}
            </Text>
          </Hint>
        );
      }
      return (
        <Text style={{ whiteSpace: 'nowrap' }} size="medium" weight={400}>
          {date.format(DEFAULT_DATE_FORMAT)}
        </Text>
      );
    }
    return '';
  }, [event.eventDate, isDateFromNow, isDateMode, isPast]);

  const isViewDetailsVisible = useMemo(() => {
    if (event.invoiceId) {
      if (event.category === IEmployerCalendarEventDtoCategoryEnum.FUNDING) {
        if (isViewPermissions) {
          return true;
        }
        return false;
      }
      return true;
    }
    return false;
  }, [event.category, event.invoiceId, isViewPermissions]);
  return (
    <Box
      data-testid={`employer-calendar-events-item_${event.id}`}
      direction="row"
      background="module"
      pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
      style={{ position: 'relative' }}
      round="container2Round"
      overflow="hidden"
    >
      <Box
        data-testid={`employer-calendar-events-item_${event.id}_indicator`}
        width="3px"
        background={currentColor}
        style={{ position: 'absolute', left: 0, top: 0, bottom: 0 }}
      />

      <Box direction="column" fill gap="spacing4">
        <Text
          data-testid={`employer-calendar-events-item_${event.id}_name`}
          size="large"
          color={isFailedMode ? 'danger' : isPast ? 'textDisabled' : 'textBody'}
          weight={700}
          truncate
          title={event.name}
        >
          {event.name}
        </Text>
        <Box>
          <Text
            data-testid={`employer-calendar-events-item_${event.id}_plan-name`}
            size="medium"
            color={isPast ? 'textDisabled' : _.get(theme.colors.cardTypes, event.plans?.[0]?.type || 'HSA')}
            weight={400}
            title={event.plans?.[0]?.name}
            truncate
          >
            {planName}
          </Text>
        </Box>
      </Box>
      <div>
        <Box gap="spacing4" align="end">
          <div data-testid={`employer-calendar-events-item_${event.id}_date`}>
            <Text
              style={{ whiteSpace: 'nowrap' }}
              color={isPast ? 'textDisabled' : 'textBody'}
              size="medium"
              weight={400}
            >
              {dateTime}
            </Text>
          </div>

          {isViewDetailsVisible && (
            <Anchor
              data-testid={`employer-calendar-events-item_${event.id}_details-link`}
              target="_blank"
              href={ROUTES.FINOPS_PREFUND_VIEW(event.invoiceId)}
            >
              <Text size="medium" weight={700} color="textAccent">
                Details
              </Text>
            </Anchor>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default EmployerCalendarEventsItem;
